import { useCallback, useEffect, useState } from 'react';
import { BarChart3, Loader2, RefreshCcw, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

/**
 * SongstatsUsageCard — admin-only card on Profile showing this month's
 * Songstats API budget consumption and cache health.
 *
 * Rendered inside the same `user?.is_admin` gate as VipManagerCard so
 * regular users don't see it. Lets the admin (soundstixx@gmail.com) see
 * "8/25 calls used, cache holding 42 tracks, resets in 12 days" at a glance
 * so they know when to upgrade to the paid Songstats tier.
 */
export default function SongstatsUsageCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/admin/songstats/usage');
      setData(d);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pct = data?.limit ? Math.min(100, Math.round((data.used / data.limit) * 100)) : 0;
  const near = data?.limit && data.remaining <= 3;

  return (
    <div
      className="mt-6 rounded-xl border border-border bg-card/40 p-3 sm:p-4"
      data-testid="songstats-usage-card"
    >
      <div className="flex items-start gap-2.5">
        <BarChart3 className="w-4 h-4 mt-0.5 text-[hsl(var(--primary))] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm font-medium text-foreground">Songstats API usage</p>
            <Button
              size="sm"
              variant="ghost"
              onClick={load}
              disabled={loading}
              className="h-6 px-2 text-[10px]"
              data-testid="songstats-usage-refresh"
            >
              {loading ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <RefreshCcw className="w-3 h-3 mr-1" />
              )}
              Refresh
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Test-tier budget. Track Deep Dives cache aggressively so most views cost 0.
            When we outgrow this, bump to the paid tier.
          </p>

          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <Stat
              label="Used this month"
              value={loading ? '\u2014' : `${data?.used ?? 0}/${data?.limit ?? 0}`}
              accent={near}
            />
            <Stat
              label="Remaining"
              value={loading ? '\u2014' : (data?.remaining ?? 0)}
              accent={near}
            />
            <Stat
              label="Tracks cached"
              value={loading ? '\u2014' : (data?.cache?.total_tracks_cached ?? 0)}
            />
          </div>

          <div className="mt-3">
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  near ? 'bg-amber-500' : 'bg-[hsl(var(--primary))]'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {data?.month && (
              <div className="text-[10px] font-mono text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                <span>Month {data.month}</span>
                {data?.cache?.fresh_tracks_cached != null && (
                  <span className="flex items-center gap-1">
                    · <Database className="w-2.5 h-2.5" />
                    {data.cache.fresh_tracks_cached} fresh (within {data.cache.ttl_days}d)
                  </span>
                )}
              </div>
            )}
          </div>

          {data?.by_endpoint && Object.keys(data.by_endpoint).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {Object.entries(data.by_endpoint).map(([ep, count]) => (
                <span
                  key={ep}
                  className="text-[10px] font-mono bg-background border border-border rounded-md px-1.5 py-0.5"
                >
                  {ep}: {count}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = false }) {
  return (
    <div className="rounded-lg bg-background border border-border py-2 px-1.5">
      <div
        className={`text-lg font-display font-semibold tabular-nums ${
          accent ? 'text-amber-500' : 'text-foreground'
        }`}
      >
        {value}
      </div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
    </div>
  );
}
